import {ASTKindToNode, ASTNode, FieldNode, FragmentDefinitionNode, InlineFragmentNode} from 'graphql';
import _ from 'underscore';

import {assert} from 'Asserts';
import {
  ExcludeCondition,
  REST_EXCLUDE_CONDITION_BY_KEY,
} from 'graphql-client/declarative-translations/RESTExcludeConditions';
import pickDefined from 'util/pickDefined';

import {ASYMMETRIC_RESOLVER_BY_KEY, AsymmetricFieldResolver} from './AsymmetricFieldResolvers';
import {DeclarativeTranslationsMapping} from './DeclarativeTranslationsMapping';
import {TranslatableValue} from './DeclarativeTranslator';
import {RESTFieldOptions, RESTSelectionSetOptions} from './Types';

const GRAPHQL_NAME_KEY = 'graphQLName';
const FIELD_TRANSLATOR_KEY = 'fieldTranslator';
const FIELD_RESOLVER_KEY = 'fieldResolver';
const FIELDS_KEY = 'fields';
const SPREADS_KEY = 'spreads';
const UNIONS_KEY = 'unions';
const REST_ONLY_FIELDS_KEY = 'restOnlyFields';
export const TYPENAME_RESOLVER_KEY = 'typenameResolver';
const EXCLUDE_CONDITION_KEY = 'excludeCondition';
const GRAPHQL_ONLY_FIELDS_KEY = 'graphQLOnlyFields';

export class HasAsymmetricFieldResolver {
  fieldName: string;
  asymmetricFieldResolver: string | null;

  constructor(fieldName: string, asymmetricFieldResolver: string | null) {
    this.fieldName = fieldName;
    this.asymmetricFieldResolver = asymmetricFieldResolver;
  }

  public getAsymmetricFieldResolver(): AsymmetricFieldResolver {
    assert(
      this.asymmetricFieldResolver != null,
      `Expected to find ${FIELD_RESOLVER_KEY} option specified for field ${this.fieldName}`
    );

    const resolver = ASYMMETRIC_RESOLVER_BY_KEY[this.asymmetricFieldResolver];

    assert(
      resolver != null,
      `Expected to find ${FIELD_RESOLVER_KEY} key='${this.asymmetricFieldResolver}' in ` +
        `CUSTOM_RESOLVER_BY_KEY for field=${this.fieldName}`
    );

    return resolver;
  }

  public resolveValue(sourceObj: TranslatableValue): unknown {
    return this.getAsymmetricFieldResolver().getValue(sourceObj);
  }
}

class GraphQLTargetField extends HasAsymmetricFieldResolver {}

export class RESTField extends HasAsymmetricFieldResolver {
  selectionSet: RESTSelectionSet | null;
  customGraphQLName: string | null;
  typenameResolverName: string | null;
  customFieldTranslator: string | null;
  asymmetricFieldResolver: string | null;
  excludeCondition: string | null;
  declarativeTranslationsMapping: DeclarativeTranslationsMapping;

  constructor(
    fieldName: string,
    selectionSet: RESTSelectionSet | null,
    customGraphQLName: string | null,
    typenameResolverName: string | null,
    customFieldTranslator: string | null,
    asymmetricFieldResolver: string | null,
    excludeCondition: string | null,
    declarativeTranslationsMapping: DeclarativeTranslationsMapping
  ) {
    super(fieldName, asymmetricFieldResolver);
    this.selectionSet = selectionSet;
    this.customGraphQLName = customGraphQLName;
    this.typenameResolverName = typenameResolverName;
    this.customFieldTranslator = customFieldTranslator;
    this.asymmetricFieldResolver = asymmetricFieldResolver;
    this.excludeCondition = excludeCondition;
    this.declarativeTranslationsMapping = declarativeTranslationsMapping;
  }

  public getExcludeCondition(): ExcludeCondition | null {
    return this.excludeCondition && REST_EXCLUDE_CONDITION_BY_KEY[this.excludeCondition]
      ? REST_EXCLUDE_CONDITION_BY_KEY[this.excludeCondition]
      : null;
  }

  private static validateRESTField(fieldName: string, fieldOptions: RESTFieldOptions): void {
    const validKeys = [
      GRAPHQL_NAME_KEY,
      FIELD_TRANSLATOR_KEY,
      FIELD_RESOLVER_KEY,
      FIELDS_KEY,
      SPREADS_KEY,
      UNIONS_KEY,
      REST_ONLY_FIELDS_KEY,
      TYPENAME_RESOLVER_KEY,
      EXCLUDE_CONDITION_KEY,
    ];

    const invalidKeys = Object.keys(fieldOptions).filter((key) => !validKeys.includes(key));
    assert(!invalidKeys.length, `Unexpected option(s) for field "${fieldName}": ${invalidKeys.join(', ')}`);
  }

  public static createFromJSONField(
    fieldName: string,
    fieldOptions: RESTFieldOptions,
    declarativeTranslationsMapping: DeclarativeTranslationsMapping
  ): RESTField {
    this.validateRESTField(fieldName, fieldOptions);

    const optionsForNestedSelection: RESTSelectionSetOptions = pickDefined(
      fieldOptions,
      FIELDS_KEY,
      SPREADS_KEY,
      UNIONS_KEY,
      REST_ONLY_FIELDS_KEY
    );
    if (Object.keys(optionsForNestedSelection).length && fieldOptions[TYPENAME_RESOLVER_KEY] != null) {
      // A typenameResolver alone isn't a reason to create a nested selection set (there's nothing to
      // select), but if there is a nested selection set, we should attach the typenameResolver to it.
      optionsForNestedSelection[TYPENAME_RESOLVER_KEY] = fieldOptions[TYPENAME_RESOLVER_KEY];
    }

    return new RESTField(
      fieldName,
      _.isEmpty(optionsForNestedSelection)
        ? null
        : RESTSelectionSet.parse(optionsForNestedSelection, declarativeTranslationsMapping),
      fieldOptions[GRAPHQL_NAME_KEY] ?? null,
      fieldOptions[TYPENAME_RESOLVER_KEY] ?? null,
      fieldOptions[FIELD_TRANSLATOR_KEY] ?? null,
      fieldOptions[FIELD_RESOLVER_KEY] ?? null,
      fieldOptions[EXCLUDE_CONDITION_KEY] ?? null,
      declarativeTranslationsMapping ?? null
    );
  }
}

export class RESTSelectionSet {
  fieldsByName: {[fieldName: string]: RESTField};
  typenameResolverName: string | null;
  restOnlyFieldsByName: {[fieldName: string]: RESTField};
  graphQLOnlyFieldsByName: {[fieldName: string]: GraphQLTargetField};
  declarationSpreads: {[spreadName: string]: RESTSelectionSet};
  declarationUnions: {[unionTypename: string]: RESTSelectionSet};
  declarativeTranslationsMapping: DeclarativeTranslationsMapping | null;

  constructor(
    fieldsByName: {[fieldName: string]: RESTField},
    typenameResolverName: string | null,
    restOnlyFieldsByName: {[fieldName: string]: RESTField},
    graphQLOnlyFieldsByName: {[fieldName: string]: GraphQLTargetField},
    declarationSpreads: {[spreadName: string]: RESTSelectionSet},
    declarationUnions: {[unionTypename: string]: RESTSelectionSet},
    declarativeTranslationsMapping: DeclarativeTranslationsMapping | null
  ) {
    this.fieldsByName = fieldsByName;
    this.typenameResolverName = typenameResolverName;
    this.restOnlyFieldsByName = restOnlyFieldsByName;
    this.graphQLOnlyFieldsByName = graphQLOnlyFieldsByName;
    this.declarationSpreads = declarationSpreads;
    this.declarationUnions = declarationUnions;
    this.declarativeTranslationsMapping = declarativeTranslationsMapping;
  }

  private static validateRESTObj(restObj: RESTSelectionSetOptions): void {
    assert(_.isObject(restObj), 'Malformed REST JSON - should be an object');
    const validTopLevelKeys = [FIELDS_KEY, SPREADS_KEY, UNIONS_KEY];
    assert(
      // @ts-expect-error TS doesn't like accessing restObj as a dictionary
      validTopLevelKeys.some((key) => restObj[key] != null),
      `Expected one of the following top level keys: ${validTopLevelKeys
        .map((key) => `'${key}'`)
        .join(', ')} - found keys ${Object.keys(restObj)}`
    );

    const validKeys = [
      FIELDS_KEY,
      SPREADS_KEY,
      UNIONS_KEY,
      REST_ONLY_FIELDS_KEY,
      GRAPHQL_ONLY_FIELDS_KEY,
      TYPENAME_RESOLVER_KEY,
    ];
    const invalidKeys = Object.keys(restObj).filter((key) => !validKeys.includes(key));
    assert(invalidKeys.length === 0, `Unexpected option(s) in selection set: ${invalidKeys}`);
  }

  public static parse(
    restObj: RESTSelectionSetOptions,
    declarativeTranslationsMapping: DeclarativeTranslationsMapping
  ): RESTSelectionSet {
    this.validateRESTObj(restObj);

    const parsedFieldsByName = _.mapObject(restObj[FIELDS_KEY] ?? {}, (fieldOptions, fieldName) =>
      RESTField.createFromJSONField(fieldName, fieldOptions, declarativeTranslationsMapping)
    );

    const parsedRESTOnlyFieldsByName = _.mapObject(
      restObj[REST_ONLY_FIELDS_KEY] ?? {},
      (fieldOptions, fieldName) =>
        RESTField.createFromJSONField(fieldName, fieldOptions, declarativeTranslationsMapping)
    );

    const parsedGraphQLOnlyFieldsByName = _.mapObject(
      restObj[GRAPHQL_ONLY_FIELDS_KEY] ?? {},
      (fieldOptions, fieldName) => new GraphQLTargetField(fieldName, fieldOptions[FIELD_RESOLVER_KEY])
    );

    // Spreads don't have any options yet
    const parsedDeclarationSpreads = _.mapObject(restObj[SPREADS_KEY] ?? {}, (spreadOptions, spreadName) =>
      RESTSelectionSet.parse(
        declarativeTranslationsMapping.getRESTDeclaration(spreadName),
        declarativeTranslationsMapping
      )
    );

    const parsedDeclarationUnions = _.mapObject(restObj[UNIONS_KEY] ?? {}, (unionDeclaration) =>
      RESTSelectionSet.parse(unionDeclaration, declarativeTranslationsMapping)
    );

    return new RESTSelectionSet(
      parsedFieldsByName,
      restObj[TYPENAME_RESOLVER_KEY] ?? null,
      parsedRESTOnlyFieldsByName,
      parsedGraphQLOnlyFieldsByName,
      parsedDeclarationSpreads,
      parsedDeclarationUnions,
      declarativeTranslationsMapping
    );
  }
}

interface GraphQLFieldsMap {
  [fieldName: string]: FieldNode;
}

function ofType<K extends keyof ASTKindToNode>(
  nodes: ReadonlyArray<ASTNode>,
  kind: K
): Array<ASTKindToNode[K]> {
  // @ts-expect-error
  return nodes.flatMap((node) => (node.kind === kind ? [node] : []));
}

export class GraphQLSelectionSet {
  typename: string | null;
  fieldsByName: GraphQLFieldsMap;
  fragmentSpreads: {[fragmentName: string]: GraphQLSelectionSet};
  inlineFragments: {[typeCondition: string]: GraphQLSelectionSet};
  declarativeTranslationsMapping: DeclarativeTranslationsMapping;

  constructor(
    typename: string | null,
    fieldsByName: GraphQLFieldsMap,
    fragmentSpreads: {[fragmentName: string]: GraphQLSelectionSet},
    inlineFragments: {[typeCondition: string]: GraphQLSelectionSet},
    declarativeTranslationsMapping: DeclarativeTranslationsMapping
  ) {
    this.typename = typename;
    this.fieldsByName = fieldsByName;
    this.fragmentSpreads = fragmentSpreads;
    this.inlineFragments = inlineFragments;
    this.declarativeTranslationsMapping = declarativeTranslationsMapping;
  }

  public getSpreadByDeclarationName(declarationName: string): GraphQLSelectionSet {
    // TODO: This isn't great - it duplicates knowledge of our naming convention with
    // DeclarativeTranslationsMapping.
    return this.fragmentSpreads[`${declarationName}GraphQL`];
  }

  private static validate(
    selectionSetContainer: FieldNode | FragmentDefinitionNode | InlineFragmentNode
  ): void {
    const selections = selectionSetContainer.selectionSet!.selections;
    const fields = ofType(selections, 'Field');
    const aliases = fields.filter((f) => f.alias != null).map((f) => f.alias);

    assert(aliases.length === 0, 'GraphQL aliases are currently unsupported.');
  }

  public static create(
    selectionSetContainer: FieldNode | FragmentDefinitionNode | InlineFragmentNode,
    declarativeTranslationsMapping: DeclarativeTranslationsMapping
  ): GraphQLSelectionSet {
    this.validate(selectionSetContainer);

    const typename =
      selectionSetContainer.kind === 'FragmentDefinition'
        ? selectionSetContainer.typeCondition.name.value
        : null;

    const selections = selectionSetContainer.selectionSet!.selections;
    const fragmentSpreads = _.object(
      ofType(selections, 'FragmentSpread').map((selection) => [
        selection.name.value,
        GraphQLSelectionSet.create(
          declarativeTranslationsMapping.getParsedGraphQLFragmentFromName(selection.name.value),
          declarativeTranslationsMapping
        ),
      ])
    );

    const inlineFragments = _.object(
      ofType(selections, 'InlineFragment').map((selection) => [
        selection.typeCondition?.name.value,
        GraphQLSelectionSet.create(selection, declarativeTranslationsMapping),
      ])
    );

    return new GraphQLSelectionSet(
      typename,
      _.indexBy(ofType(selections, 'Field'), (selection) => selection.name.value),
      fragmentSpreads,
      inlineFragments,
      declarativeTranslationsMapping
    );
  }
}
